import ChipRangeInput from "@/components/ChipRangeInput";
import ChipSelect from "@/components/ChipSelect";
import { sectorOptions } from "@/components/SectorSelect";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { CampaignType, Client, Platform } from "../../../shared/types";
import { abbreviateNumber } from "../../../utils/formatNumbers";
import { emptyOpportunityListFilter, type OpportunityListFilter } from "../list";

const CAMPAIGN_TYPES: CampaignType[] = ["Song", "Brand"];
const PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"];

type OpportunityFilterSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    value: OpportunityListFilter;
    onChange: (value: OpportunityListFilter) => void;
};

/** Index filters: ChipSelect for type / platform / sector, ChipRangeInput for overlapping price. */
export function OpportunityFilterSheet({ open, onOpenChange, value, onChange }: OpportunityFilterSheetProps) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="sm:max-w-xl">
                <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="min-h-0 flex-1 overflow-y-auto px-4">
                    {/* Chip row: wrap existing ChipSelect / ChipRangeInput — same as TalentFilterSheet. */}
                    <div className="flex flex-wrap gap-y-2">
                        <ChipSelect
                            label={value.campaignTypes.length ? `Campaign type: ${value.campaignTypes.length}` : "Campaign type"}
                            active={value.campaignTypes.length > 0}
                            options={CAMPAIGN_TYPES}
                            value={value.campaignTypes}
                            onChange={(next) => onChange({ ...value, campaignTypes: next as CampaignType[] })}
                        />
                        <ChipSelect
                            label={value.platforms.length ? `Platform: ${value.platforms.length}` : "Platform"}
                            active={value.platforms.length > 0}
                            options={PLATFORMS}
                            value={value.platforms}
                            onChange={(next) => onChange({ ...value, platforms: next as Platform[] })}
                        />
                        <ChipSelect
                            label={
                                value.companySectors.length
                                    ? `Company sector: ${value.companySectors.length}`
                                    : "Company sector"
                            }
                            active={value.companySectors.length > 0}
                            options={[...sectorOptions]}
                            value={value.companySectors}
                            onChange={(next) =>
                                onChange({ ...value, companySectors: next as NonNullable<Client["companySector"]>[] })
                            }
                        />
                        <ChipRangeInput
                            label={priceChipLabel(value.priceRange)}
                            value={[value.priceRange.lower, value.priceRange.upper]}
                            onChange={(next) => onChange({ ...value, priceRange: { lower: next.lower, upper: next.upper } })}
                            startAddon="$"
                            min={0}
                            minPlaceholder="Min"
                            maxPlaceholder="Max"
                        />
                    </div>
                </div>
                <SheetFooter className="flex-row justify-end">
                    <Button type="button" variant="outline" onClick={() => onChange(emptyOpportunityListFilter)}>
                        Clear all
                    </Button>
                    <Button type="button" onClick={() => onOpenChange(false)}>
                        Done
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

function priceChipLabel(range: OpportunityListFilter["priceRange"]): string {
    const { lower, upper } = range;
    if (lower == null && upper == null) {
        return "Price";
    }
    if (lower != null && upper != null) {
        return `Price: $${abbreviateNumber(lower)}-$${abbreviateNumber(upper)}`;
    }
    if (lower != null) {
        return `Price: $${abbreviateNumber(lower)}+`;
    }
    return `Price: up to $${abbreviateNumber(upper!)}`;
}
